/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Campaign, Lookup, ResponseCode } from "../../api/types/types";

import { getValuesForResponseCode, parseResponseCodeValue } from "./campaign-response-lookup-utils";
import {
  buildCampaignFieldPath,
  CAMPAIGN_FIELD_NAMES,
  COMPETITOR_WHITELIST_TYPES,
} from "./constants";
import { CampaignFormData } from "./types";

/** Notes are required for Won (1), Lost (2), and To be confirmed (3).
 * This is a sub-optimal solution until a time that this link between response code and notes can be recieved through the API.
 */
const NOTES_REQUIRED_RESPONSE_CODES = [
  ResponseCode.Won,
  ResponseCode.Lost,
  ResponseCode.ToBeConfirmed,
];

/**
 * Checks if notes are required for a given response code
 * Notes are required for Won (1), Lost (2), and To be confirmed (3)
 * @param responseCodeValue - The response code value
 * @returns True if notes are required, false otherwise
 */
export const isNotesRequired = (responseCodeValue: string | null | undefined): boolean => {
  const codeValue = parseResponseCodeValue(responseCodeValue);

  if (codeValue === null) {
    return false;
  }

  return NOTES_REQUIRED_RESPONSE_CODES.includes(codeValue);
};

export interface ValidationError {
  field: string;
  message: string;
}

export const validateFormData = (
  campaigns: Campaign[],
  formData: Record<string, CampaignFormData>,
  reasonLookup: Lookup | undefined,
  typeLookup: Lookup | undefined,
  competitorWhitelistTypes: string[] = COMPETITOR_WHITELIST_TYPES
): ValidationError[] => {
  const errors: ValidationError[] = [];

  campaigns.forEach((campaign, index) => {
    const campaignKey = campaign.campaignId || index.toString();
    const data = formData[campaignKey];
    if (!data) {
      return;
    }

    if (data.responseCode) {
      const validReasonValues = getValuesForResponseCode(reasonLookup, data.responseCode);
      const hasReasons = validReasonValues.length > 0;

      const validTypeValues = getValuesForResponseCode(typeLookup, data.responseCode);
      const hasTypes = validTypeValues.length > 0;

      if (hasReasons && !data.reason) {
        errors.push({
          field: buildCampaignFieldPath(campaignKey, CAMPAIGN_FIELD_NAMES.REASON),
          message: "Reason is required when response code is selected",
        });
      }

      if (hasTypes && !data.type) {
        errors.push({
          field: buildCampaignFieldPath(campaignKey, CAMPAIGN_FIELD_NAMES.TYPE),
          message: "Type is required when response code is selected",
        });
      }

      if (isNotesRequired(data.responseCode) && !data.notes?.trim()) {
        errors.push({
          field: buildCampaignFieldPath(campaignKey, CAMPAIGN_FIELD_NAMES.NOTES),
          message: "Notes are required for this response code",
        });
      }
    }

    if (
      data.responseCode &&
      data.type &&
      competitorWhitelistTypes.includes(data.type) &&
      !data.competitor
    ) {
      errors.push({
        field: buildCampaignFieldPath(campaignKey, CAMPAIGN_FIELD_NAMES.COMPETITOR),
        message: "Competitor is required for this type",
      });
    }
  });

  return errors;
};
